import * as React from 'react';
import {
  Image,
  MediaPlayer,
  PDFReader,
} from '@uncinc/uncinc-react-kitchen-sink';
import { MimeType } from '@uncinc/uncinc-react-kitchen-sink/dist/components/MediaPlayer/types';

import { MediaEntity, MediaReferenceField } from '@uncinc/react-drupal-core/dist/types';
import parseEmbedUrl from '@uncinc/react-drupal-core/dist/helpers/parseEmbedUrl';

const ASPECT_RATIO_ORIGINAL = 'original';

export type AspectRatio = '1_1' | '1_2' | '1_3' | '2_1' | '2_3' | '3_1' | '3_2' | '3_4' | '4_3' | '16_9' | 'original';
export type SizeOptions = 'xxs' | 'xs' | 'sm' | 'md' | 'lg';

/**
 * These 3 types below are used as parameters for the MediaReferenceField type.
 */
type SupportedBundles = 'document' | 'video_file' | 'video' | 'audio' | 'image' | 'svg';
type RequiredImageStyles = `ratio_${AspectRatio}_${SizeOptions}`;
type MappedMediaBundle = {
  [key in SupportedBundles]: MediaEntity;
};

interface DynamicMediaItemProps {
  media: MediaReferenceField<MappedMediaBundle, SupportedBundles, RequiredImageStyles>;
  imageAspectRatio?: AspectRatio;
  className?: string;
  caption?: string;
  alt?: string;
  posterImage?: string;
  aboveTheFold?: boolean;
}

const DynamicMediaItem: React.FC<DynamicMediaItemProps> = (props) => {
  const {
    media,
    imageAspectRatio = ASPECT_RATIO_ORIGINAL,
    className,
    caption,
    posterImage = '',
    alt = '',
    aboveTheFold = false,
  } = props;

  switch (media.bundle) {
    case 'svg': {
      return <img className={className} src={media.source} alt={caption} />;
    }

    case 'document': {
      return (
        <PDFReader
          // When developing locally, there's a fair chance you'll run into CORS errors
          // You can then use this url, for example:
          // url="https://cdn.filestackcontent.com/wcrjf9qPTCKXV3hMXDwK"
          url={media.source}
        />
      );
    }

    case 'audio':
    case 'video_file':
    case 'video': {
      let mimeType;
      if (media.bundle === 'video_file') {
        // @ts-expect-error
        mimeType = parseEmbedUrl(media.embed).mime;
      } else {
        mimeType = media.mime_type;
      }

      return (
        <>
          <MediaPlayer
            posterUrl={posterImage}
            // @ts-expect-error
            src={media.bundle === 'video_file' ? media.embed : media.source}
            type={mimeType as MimeType}
          />
          {caption && (
            <p className="caption pt-3 mb-0">{caption}</p>
          )}
        </>
      );
    }

    case 'image': {
      const sizeOptions: SizeOptions[] = ['xxs', 'xs', 'sm', 'md', 'lg'];
      // Your Drupal imagestyles should correspond te following widths in pixels:
      // 128, 256, 512, 1024, 2048

      let src;
      if (aboveTheFold) {
        // For images above the fold, adaptive gives poor LCP score,
        // so fall back to a Picture component instead.

        // For the Picture component, we should always pass one size lárger
        // than the current minimum screen width.
        // So, if you're screen is larger than 512, we should at least pass 1024.
        const sizes = {
          xxs: 0,
          xs: 128,
          sm: 256,
          md: 512,
          lg: 1024,
        };

        src = sizeOptions.reduce((accumulator, size) => {
          const imageStyle = media.styles?.[`ratio_${imageAspectRatio}_${size}`];

          // @ts-expect-error
          accumulator[sizes[size]] = `${imageStyle || media.source}`;

          return accumulator;
        }, {});
      } else {
        src = sizeOptions.map((size) => {
          const imageStyle = media.styles?.[`ratio_${imageAspectRatio}_${size}`];
          return `${imageStyle || media.source}`;
        });
      }

      // The AdaptivePicture picture component needs a number as a ratio.
      let ratio;
      const [antecedent, consequent] = imageAspectRatio.split('_');
      if (antecedent && consequent) {
        ratio = parseInt(antecedent, 10) / parseInt(consequent, 10);
      }

      // Set to auto if we want to display the original aspect ratio.
      if (imageAspectRatio === ASPECT_RATIO_ORIGINAL) {
        ratio = 'auto';
      }

      return (
        <div className={className}>
          <Image
            src={src}
            alt={alt || media.alt || media.title || caption}
            lazyload={false}
            aspectRatio={ratio}
          />
          {caption && (
            <p className="caption">{caption}</p>
          )}
        </div>
      );
    }

    default:
      return null;
  }
};

export default DynamicMediaItem;
